// components/admin/UserManagementScreen.js
import React, { useState, useEffect } from 'react';
import LoginLogsScreen from './LoginLogsScreen';
import { useAlert } from '../common/AppAlert';

const UserManagementScreen = ({ user, token, onBack, isDarkMode, api }) => {
  const { prompt } = useAlert();
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '',
    displayName: '',
    password: '',
    isAdmin: false
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showLoginLogs, setShowLoginLogs] = useState(false);

  const themeStyles = {
    background: isDarkMode ? '#1a1a1a' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000',
    inputBg: isDarkMode ? '#2d2d2d' : '#ffffff',
    inputBorder: isDarkMode ? '#404040' : '#d1d5db',
    buttonBg: isDarkMode ? '#374151' : '#e5e7eb',
    buttonBorder: isDarkMode ? '#4b5563' : '#d1d5db',
    tableHeaderBg: isDarkMode ? '#2d2d2d' : '#f2f2f2',
    tableBorder: isDarkMode ? '#404040' : '#000000',
  };

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/admin/users', token);
      setUsers(response);
    } catch (error) {
      setError('Failed to load users');
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.post('/admin/users', newUser, token);
      setSuccess('User added successfully');
      setNewUser({ username: '', displayName: '', password: '', isAdmin: false });
      loadUsers();
    } catch (error) {
      setError('Failed to add user');
    }
  };

  const handleLockUser = async (userId, isLocked) => {
    try {
      await api.put(`/admin/users/${userId}/lock`, { isLocked: !isLocked }, token);
      loadUsers();
    } catch (error) {
      setError('Failed to update user status');
    }
  };

  const handleResetPassword = async (userId) => {
    const newPassword = await prompt('Enter new password:', 'Reset User Password');
    if (newPassword) {
      try {
        await api.put(`/admin/users/${userId}/password`, { password: newPassword }, token);
        setSuccess('Password reset successfully');
      } catch (error) {
        setError('Failed to reset password');
      }
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await api.put(`/admin/users/${userId}/admin`, { isAdmin: !isAdmin }, token);
      loadUsers();
    } catch (error) {
      setError('Failed to update admin status');
    }
  };

  // Show login logs screen
  if (showLoginLogs) {
    return (
      <LoginLogsScreen
        user={user}
        token={token}
        onBack={() => setShowLoginLogs(false)}
        isDarkMode={isDarkMode}
        api={api}
      />
    );
  }

  return (
    <div style={{
      backgroundColor: themeStyles.background,
      color: themeStyles.color,
      minHeight: '100vh',
      padding: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>User Management</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setShowLoginLogs(true)}
            style={{
              padding: '8px 16px',
              backgroundColor: themeStyles.buttonBg,
              color: themeStyles.color,
              border: `1px solid ${themeStyles.buttonBorder}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            View Login Logs
          </button>
          <button
            onClick={onBack}
            style={{
              padding: '8px 16px',
              backgroundColor: themeStyles.buttonBg,
              color: themeStyles.color,
              border: `1px solid ${themeStyles.buttonBorder}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back to Projects
          </button>
        </div>
      </div>

      {/* Add New User Form */}
      <div style={{
        backgroundColor: isDarkMode ? '#2d2d2d' : '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px'
      }}>
        <h2 style={{ marginBottom: '20px' }}>Add New User</h2>
        <form onSubmit={handleAddUser} style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: themeStyles.color }}>
              Username:
            </label>
            <input
              type="text"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              style={{
                padding: '8px',
                border: `1px solid ${themeStyles.inputBorder}`,
                borderRadius: '4px',
                backgroundColor: themeStyles.inputBg,
                color: themeStyles.color
              }}
              required
            />
          </div>
          
          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: themeStyles.color }}>
              Display Name:
            </label>
            <input
              type="text"
              value={newUser.displayName}
              onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })}
              style={{
                padding: '8px',
                border: `1px solid ${themeStyles.inputBorder}`,
                borderRadius: '4px',
                backgroundColor: themeStyles.inputBg,
                color: themeStyles.color
              }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', color: themeStyles.color }}>
              Password:
            </label>
            <input
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              style={{
                padding: '8px',
                border: `1px solid ${themeStyles.inputBorder}`,
                borderRadius: '4px',
                backgroundColor: themeStyles.inputBg,
                color: themeStyles.color
              }}
              required
            />
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', color: themeStyles.color }}>
              <input
                type="checkbox"
                checked={newUser.isAdmin}
                onChange={(e) => setNewUser({ ...newUser, isAdmin: e.target.checked })}
                style={{ marginRight: '8px' }}
              />
              Administrator
            </label>
          </div>

          <button
            type="submit"
            style={{
              padding: '8px 16px',
              backgroundColor: themeStyles.buttonBg,
              color: themeStyles.color,
              border: `1px solid ${themeStyles.buttonBorder}`,
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Add User
          </button>
        </form>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          color: '#dc2626',
          marginBottom: '20px',
          padding: '10px',
          backgroundColor: '#fee2e2',
          borderRadius: '4px'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          color: '#166534',
          marginBottom: '20px',
          padding: '10px',
          backgroundColor: '#bbf7d0',
          borderRadius: '4px'
        }}>
          {success}
        </div>
      )}

      {/* Users Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{
              border: `1px solid ${themeStyles.tableBorder}`,
              padding: '12px',
              backgroundColor: themeStyles.tableHeaderBg,
              color: themeStyles.color,
              textAlign: 'left'
            }}>
              Username
            </th>
            <th style={{
              border: `1px solid ${themeStyles.tableBorder}`,
              padding: '12px',
              backgroundColor: themeStyles.tableHeaderBg,
              color: themeStyles.color,
              textAlign: 'left'
            }}>
              Display Name
            </th>
            <th style={{
              border: `1px solid ${themeStyles.tableBorder}`,
              padding: '12px',
              backgroundColor: themeStyles.tableHeaderBg,
              color: themeStyles.color,
              textAlign: 'left'
            }}>
              Status
            </th>
            <th style={{
              border: `1px solid ${themeStyles.tableBorder}`,
              padding: '12px',
              backgroundColor: themeStyles.tableHeaderBg,
              color: themeStyles.color,
              textAlign: 'left'
            }}>
              Admin
            </th>
            <th style={{
              border: `1px solid ${themeStyles.tableBorder}`,
              padding: '12px',
              backgroundColor: themeStyles.tableHeaderBg,
              color: themeStyles.color,
              textAlign: 'left'
            }}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {users.map((userItem) => (
            <tr key={userItem.id}>
              <td style={{
                border: `1px solid ${themeStyles.tableBorder}`,
                padding: '12px',
                color: themeStyles.color
              }}>
                {userItem.username}
              </td>
              <td style={{
                border: `1px solid ${themeStyles.tableBorder}`,
                padding: '12px',
                color: themeStyles.color
              }}>
                {userItem.display_name}
              </td>
              <td style={{
                border: `1px solid ${themeStyles.tableBorder}`,
                padding: '12px',
                color: themeStyles.color
              }}>
                {userItem.is_locked ? 'Locked' : 'Active'}
              </td>
              <td style={{
                border: `1px solid ${themeStyles.tableBorder}`,
                padding: '12px',
                color: themeStyles.color
              }}>
                {userItem.is_admin ? 'Yes' : 'No'}
              </td>
              <td style={{
                border: `1px solid ${themeStyles.tableBorder}`,
                padding: '12px',
                color: themeStyles.color
              }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleLockUser(userItem.id, userItem.is_locked)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: themeStyles.buttonBg,
                      color: themeStyles.color,
                      border: `1px solid ${themeStyles.buttonBorder}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    {userItem.is_locked ? 'Unlock' : 'Lock'}
                  </button>
                  
                  <button
                    onClick={() => handleResetPassword(userItem.id)}
                    style={{
                      padding: '4px 8px',
                      backgroundColor: themeStyles.buttonBg,
                      color: themeStyles.color,
                      border: `1px solid ${themeStyles.buttonBorder}`,
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Reset Password
                  </button>
                  
                  {userItem.username !== user.username && (
                    <button
                      onClick={() => handleToggleAdmin(userItem.id, userItem.is_admin)}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: themeStyles.buttonBg,
                        color: themeStyles.color,
                        border: `1px solid ${themeStyles.buttonBorder}`,
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      {userItem.is_admin ? 'Remove Admin' : 'Make Admin'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserManagementScreen;